/**
 * Input validation utilities for security and data integrity.
 */

/** Check if a string is a valid email. */
export function isValidEmail(email: string): boolean {
  if (typeof email !== 'string' || !email) return false;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email.trim());
}

/** Check if a string is a valid http(s) URL. */
export function isValidUrl(url: string): boolean {
  if (typeof url !== 'string' || !url) return false;

  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Check if a string is a safe relative URL (rejects absolute/protocol URLs and script/handler injection patterns). */
export function isSafeRelativeUrl(path: string): boolean {
  if (typeof path !== 'string' || !path) return false;

  if (!path.startsWith('/')) return false;

  if (path.includes('://')) return false;

  const dangerous = ['javascript:', 'data:', 'vbscript:', '<script', 'onerror=', 'onclick='];
  return !dangerous.some(pattern => path.toLowerCase().includes(pattern));
}

/** Validate password strength. */
export function validatePasswordStrength(password: string): {
  isValid: boolean;
  messages: string[];
  strength: 'weak' | 'medium' | 'strong';
} {
  const messages: string[] = [];
  let strength: 'weak' | 'medium' | 'strong' = 'weak';

  if (typeof password !== 'string' || !password) {
    return { isValid: false, messages: ['Password is required'], strength };
  }

  if (password.length < 8) {
    messages.push('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    messages.push('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    messages.push('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    messages.push('Password must contain at least one number');
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    messages.push('Password must contain at least one special character');
  }

  const criteriaMet = 5 - messages.length;
  if (criteriaMet >= 5 && password.length >= 12) {
    strength = 'strong';
  } else if (criteriaMet >= 3 && password.length >= 8) {
    strength = 'medium';
  }

  return {
    isValid: messages.length === 0,
    messages,
    strength,
  };
}

/** Validate username format: 3-20 chars, alphanumeric/underscore/hyphen. */
export function isValidUsername(username: string): boolean {
  if (typeof username !== 'string' || !username) return false;

  const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
  return usernameRegex.test(username);
}

/** Validate phone number format (10-15 digits). */
export function isValidPhone(phone: string): boolean {
  if (typeof phone !== 'string' || !phone) return false;

  const phoneRegex = /^\+?[0-9]{10,15}$/;
  const cleaned = phone.replace(/[\s()-]/g, '');
  return phoneRegex.test(cleaned);
}

/** Check if input contains SQL injection patterns. */
export function containsSqlInjection(input: string): boolean {
  if (typeof input !== 'string') return false;

  const sqlPatterns = [
    /(\bunion\b.*\bselect\b)/i,
    /(\bselect\b.*\bfrom\b)/i,
    /(\binsert\b.*\binto\b)/i,
    /(\bdelete\b.*\bfrom\b)/i,
    /(\bdrop\b.*\btable\b)/i,
    /(\bupdate\b.*\bset\b)/i,
    /(\bexec\b|\bexecute\b)/i,
    /(--|#|\/\*|\*\/)/,
    /(\bor\b.*=.*)/i,
    /('.*\b(or|and)\b.*')/i,
  ];

  return sqlPatterns.some(pattern => pattern.test(input));
}

/** Check if input contains XSS patterns (script tags, event handlers, dangerous protocols/elements). */
export function containsXss(input: string): boolean {
  if (typeof input !== 'string') return false;

  const xssPatterns = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=\s*["'][^"']*["']/gi,
    /<iframe/gi,
    /<object/gi,
    /<embed/gi,
    /eval\s*\(/gi,
    /expression\s*\(/gi,
    /vbscript:/gi,
    /data:text\/html/gi,
  ];

  return xssPatterns.some(pattern => pattern.test(input));
}

/** Check if input contains path traversal patterns (../, encoded variants). */
export function containsPathTraversal(input: string): boolean {
  if (typeof input !== 'string') return false;

  const pathTraversalPatterns = [
    /\.\.\//,
    /\.\.\\/,
    /%2e%2e%2f/i,
    /%2e%2e\\/i,
    /\.\.%2f/i,
    /\.\.%5c/i,
  ];

  return pathTraversalPatterns.some(pattern => pattern.test(input));
}

/** Validate file extension against an allowlist. */
export function isAllowedFileExtension(
  filename: string,
  allowedExtensions: string[]
): boolean {
  if (typeof filename !== 'string' || !filename) return false;

  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  return allowedExtensions.map(e => e.toLowerCase()).includes(ext);
}

/** Validate file size against a max limit. */
export function isValidFileSize(size: number, maxSize: number): boolean {
  return typeof size === 'number' && size > 0 && size <= maxSize;
}

/** Validate JSON structure. */
export function isValidJson(jsonString: string): boolean {
  if (typeof jsonString !== 'string') return false;

  try {
    JSON.parse(jsonString);
    return true;
  } catch {
    return false;
  }
}

/** Validate UUID format. */
export function isValidUuid(uuid: string): boolean {
  if (typeof uuid !== 'string') return false;

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/** Validate date string. */
export function isValidDate(dateString: string): boolean {
  if (typeof dateString !== 'string') return false;

  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
}
